import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownTrayIcon, ExclamationTriangleIcon, ChevronRightIcon, ChevronDownIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { useIsps, useIspStockCounts } from '../hooks/useIspStockCounts';

// ISP stock counts, for ordering.
//
// This is the "331. Netomnia Stock Count" spreadsheet, built from the counts
// themselves instead of pasted in each week. The arithmetic is the sheet's own:
// average weekly usage = (oldest count - newest count) / 3.
//
// The sheet puts SKUs across the top and counts down the side. With 81 items
// that means a table 81 columns wide, so this turns it on its side: an item per
// row, the three weeks as columns. Same numbers, readable on a laptop, and it
// sorts - which the sheet never could.

const fmtDate = (d) => (d
  ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB',
    { day: '2-digit', month: '2-digit', year: 'numeric' })
  : '—');

const qty = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-GB'));

// Two decimals, and never "-0". A negative average means stock arrived faster
// than it went out over the window, which is worth seeing rather than clamping.
// The raw value is kept in the data so the workbook can carry full precision
// behind a display format.
const dp2 = (v) => {
  if (v === null || v === undefined) return '—';
  const n = Math.round(Number(v) * 100) / 100;
  return (Object.is(n, -0) ? 0 : n).toLocaleString('en-GB',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function saveBlob(name, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Palette lifted from the ordering sheet so the download looks like the thing
// it replaces: grey column headers, a blue row to type the order into.
const FILL = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
const GREY = FILL('FFD9D9D9');
const BLUE = FILL('FFBDD7EE');
const AMBER = FILL('FFFFF2CC');
const ROSE = FILL('FFF8CBAD');
const VIOLET = FILL('FFE4DFEC');
const GREEN = FILL('FFC6EFCE');
const THIN = { style: 'thin', color: { argb: 'FFBFBFBF' } };
const BOX = { top: THIN, left: THIN, bottom: THIN, right: THIN };

// exceljs is ~1MB, and nobody pays for it until they press Export.
async function writeWorkbook(fileName, sheetTitle, blocks) {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'NEO2';

  // The order list comes first, because it is the thing anyone opening this is
  // actually here to do. The count detail behind it stays on the second sheet:
  // finding the dozen items that need ordering by scrolling an 81-column grid
  // is how the spreadsheet this replaces used to work.
  const order = wb.addWorksheet('Order list', { views: [{ state: 'frozen', ySplit: 2 }] });
  const ot = order.addRow([`${sheetTitle} — what to order`]);
  ot.font = { bold: true, size: 14 };
  const oh = order.addRow(['Storeroom', 'Code', 'Item', 'On hand', 'Should hold',
    'Short', 'ORDER', 'Boxes', 'Alternative']);
  oh.eachCell((c) => {
    c.font = { bold: true, size: 10 };
    c.fill = GREY;
    c.border = BOX;
    c.alignment = { horizontal: 'center', wrapText: true };
  });

  let lines = 0;
  blocks.forEach((b) => {
    (b.orders || []).forEach((o) => {
      const row = order.addRow([b.storeroom, o.code, o.item, o.have, o.need,
        o.short, o.order, o.boxes, o.alt]);
      row.eachCell((c, col) => {
        c.border = BOX;
        if (col >= 4 && col <= 7) c.numFmt = '#,##0';
        if (col === 7) { c.fill = GREEN; c.font = { bold: true, color: { argb: 'FF006100' } }; }
        if (col === 8 && !o.boxes) { c.font = { color: { argb: 'FF9C6500' } }; }
      });
      lines += 1;
    });
  });
  if (!lines) {
    const none = order.addRow(['Nothing to order — every planned item is above its required level.']);
    none.font = { italic: true, color: { argb: 'FF808080' } };
  }
  [16, 14, 34, 11, 12, 9, 10, 16, 22].forEach((w, i) => { order.getColumn(i + 1).width = w; });

  const ws = wb.addWorksheet('Stock Count', {
    views: [{ state: 'frozen', xSplit: 2, ySplit: 1 }],
  });

  const title = ws.addRow([sheetTitle]);
  title.font = { bold: true, size: 14 };
  ws.addRow([]);

  let widest = 2;

  blocks.forEach((b) => {
    const name = ws.addRow([`${b.storeroom} Storeroom`]);
    name.font = { bold: true, size: 12, color: { argb: 'FF1F4E79' } };

    const head = ws.addRow(['Name', 'Count Date', ...b.columns]);
    widest = Math.max(widest, b.columns.length + 2);
    head.eachCell((cell) => {
      cell.font = { bold: true, size: 9 };
      cell.fill = GREY;
      cell.border = BOX;
      cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
    });
    head.height = 42;

    b.rows.forEach((r) => {
      const row = ws.addRow([r.label, r.date || '', ...r.values]);
      row.eachCell({ includeEmpty: true }, (cell, col) => {
        cell.border = BOX;
        if (col > 2) {
          cell.alignment = { horizontal: 'right' };
          // Counts are whole units; anything derived carries two decimals.
          // Excel holds the full value and only the display is rounded, so
          // summing a column still gives the exact figure.
          cell.numFmt = (r.kind === 'count' || r.kind === 'theoretical'
            || r.kind === 'shortfall' || r.kind === 'orderqty') ? '#,##0' : '#,##0.00';
        }
        if (r.kind === 'order') cell.fill = BLUE;
        if (r.kind === 'avg') { cell.fill = AMBER; cell.font = { bold: true }; }
        if (r.kind === 'check') cell.fill = ROSE;
        if (r.kind === 'theoretical') cell.fill = VIOLET;
        if (r.kind === 'orderqty') { cell.fill = GREEN; cell.font = { bold: true, color: { argb: 'FF006100' } }; }
        if (r.kind === 'boxes') { cell.fill = GREEN; cell.font = { size: 9, color: { argb: 'FF006100' } }; }
        // Negative means short by that much, which is the number to order.
        if (r.kind === 'shortfall' && col > 2) {
          cell.fill = (typeof cell.value === 'number' && cell.value < 0) ? ROSE : VIOLET;
          cell.font = { bold: true, color: { argb: (typeof cell.value === 'number' && cell.value < 0) ? 'FF9C0006' : 'FF3F3F76' } };
        }
        // A forecast at or below zero is the reason to order, so it shouts.
        if (r.kind === 'forecast' && col > 2 && typeof cell.value === 'number' && cell.value <= 0) {
          cell.font = { bold: true, color: { argb: 'FF9C0006' } };
          cell.fill = ROSE;
        }
        if (r.kind === 'count' && col <= 2) cell.font = { color: { argb: 'FF595959' } };
      });
      if (r.kind === 'order' || r.kind === 'avg') row.font = { bold: true };
    });

    ws.addRow([]);
  });

  ws.getColumn(1).width = 22;
  ws.getColumn(2).width = 13;
  for (let i = 3; i <= widest; i += 1) ws.getColumn(i).width = 15;

  const buf = await wb.xlsx.writeBuffer();
  saveBlob(fileName, new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }));
}

// One storeroom laid out the way the ordering sheet is: SKUs across the top,
// a row per count date oldest-first, a blank order row to type into, then the
// average. Exporting in any other shape would mean rebuilding their workbook.
function sheetBlock(sr) {
  const cols = sr.rows;
  const rows = [];
  [...sr.weeks].reverse().forEach((w, i) => {
    const idx = sr.weeks.length - 1 - i;          // counts[] is newest-first
    rows.push({
      kind: 'count',
      label: i === sr.weeks.length - 1 ? 'Count (latest)' : 'Count',
      date: fmtDate(w.date),
      values: cols.map((r) => r.counts[idx]),
    });
  });
  rows.push({ kind: 'avg', label: 'Avg weekly usage', values: cols.map((r) => r.avg_weekly) });
  rows.push({ kind: 'forecast', label: 'Week 4 (forecast)', values: cols.map((r) => r.forecast[0]) });
  rows.push({ kind: 'forecast', label: 'Week 5 (forecast)', values: cols.map((r) => r.forecast[1]) });
  rows.push({ kind: 'theoretical', label: 'Theoretical (should hold)', values: cols.map((r) => r.theoretical) });
  rows.push({ kind: 'shortfall', label: 'On hand − theoretical', values: cols.map((r) => r.on_hand_vs_theoretical) });
  rows.push({ kind: 'orderqty', label: 'Order (whole boxes)', values: cols.map((r) => r.order_qty) });
  rows.push({ kind: 'boxes', label: 'Boxes', values: cols.map((r) => (r.boxes ? `${r.boxes} x ${r.box_size}` : null)) });
  rows.push({ kind: 'order', label: 'Order qty (manual)', values: cols.map(() => null) });
  if (cols.some((r) => r.flag)) {
    rows.push({ kind: 'check', label: 'Check', values: cols.map((r) => (r.flag ? 'CHECK' : null)) });
  }
  // Just the items that need ordering, flattened for the order-list sheet.
  const orders = cols.filter((r) => r.order_qty).map((r) => {
    const chosen = r.order_options ? r.order_options.find((o) => o.stocked) : null;
    const others = r.order_options ? r.order_options.filter((o) => !o.stocked) : [];
    return {
      code: r.group_members ? `${r.group_members.length} codes` : r.sku,
      item: r.group_label || r.name,
      have: r.group_on_hand !== null ? r.group_on_hand : r.on_hand,
      need: r.theoretical,
      short: r.shortfall,
      order: r.order_qty,
      boxes: r.boxes ? `${r.boxes} x ${r.box_size}${chosen ? ` ${chosen.label}` : (r.box_label ? ` ${r.box_label}` : '')}` : 'no box size recorded',
      alt: others.map((o) => `${o.boxes} x ${o.box} = ${o.qty} ${o.label}`).join('; ') || '',
    };
  });

  return {
    storeroom: sr.name,
    columns: cols.map((r) => `${r.sku} - ${r.name}`),
    rows,
    orders,
  };
}

function Storeroom({ sr, ispName, open, onToggle }) {
  // Defaults to the ordering sheet's own item order, because that is how the
  // people using this already read the list. The server sends them that way,
  // so 'sheet' means leaving them alone.
  const [sort, setSort] = useState('sheet');   // sheet | avg | newest | name
  const [onlyMoving, setOnlyMoving] = useState(false);

  const rows = useMemo(() => {
    let r = sr.rows;
    if (onlyMoving) r = r.filter((x) => Number(x.avg_weekly) > 0);
    if (sort === 'sheet') return r;
    const by = {
      avg: (a, b) => (Number(b.avg_weekly) || 0) - (Number(a.avg_weekly) || 0),
      newest: (a, b) => (Number(b.counts[0]) || 0) - (Number(a.counts[0]) || 0),
      name: (a, b) => a.name.localeCompare(b.name),
    };
    return [...r].sort(by[sort]);
  }, [sr.rows, sort, onlyMoving]);

  const flagged = useMemo(() => sr.rows.filter((r) => r.flag), [sr.rows]);
  // What the forecast is for: the items that hit zero within two weeks.
  const runningOut = useMemo(
    () => sr.rows.filter((r) => r.forecast[1] !== null && r.forecast[1] <= 0), [sr.rows]);
  // Items with an actual order quantity — the badge that matches the Order
  // column, as opposed to the week-5 runout warning above it.
  const toOrder = useMemo(() => sr.rows.filter((r) => r.order_qty), [sr.rows]);

  // The API hands counts back newest-first; the table reads oldest to newest so
  // the row is a timeline running into the two forecast columns. Sorting still
  // works off the original order, where index 0 is always the latest count.
  const weeksAsc = useMemo(() => [...sr.weeks].reverse(), [sr.weeks]);

  const [saving, setSaving] = useState(false);
  const exportCsv = async () => {
    setSaving(true);
    try {
      await writeWorkbook(`${ispName} - ${sr.name} - stock count.xlsx`,
        `${ispName} Stock Count — ${sr.name}`, [sheetBlock(sr)]);
    } finally { setSaving(false); }
  };

  return (
    <section className={`overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm ${
      open ? 'mb-5' : 'mb-2'}`}>
      <header className={`flex flex-wrap items-center justify-between gap-3 bg-gray-50 px-4 py-3 ${
        open ? 'border-b border-gray-200' : ''}`}>
        <button
          type="button" onClick={onToggle} aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {open
            ? <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
            : <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" />}
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-base font-bold text-gray-900">{sr.name}</span>
              {/* The whole point of collapsing is that a bad count must still
                  be visible without opening the card. */}
              {sr.skipped.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-rose-800">
                  <ExclamationTriangleIcon className="h-3 w-3" />
                  {sr.skipped.length} bad count{sr.skipped.length === 1 ? '' : 's'}
                </span>
              )}
              {flagged.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-rose-800">
                  <ExclamationTriangleIcon className="h-3 w-3" />
                  {flagged.length} item{flagged.length === 1 ? '' : 's'} to check
                </span>
              )}
              {toOrder.length > 0 && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-emerald-900">
                  {toOrder.length} to order
                </span>
              )}
              {runningOut.length > 0 && (
                <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-sky-900">
                  {runningOut.length} run out by wk 5
                </span>
              )}
              {!sr.complete && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-800">
                  no average
                </span>
              )}
            </span>
            <span className="block text-xs text-gray-500">
              {sr.weeks.length} count{sr.weeks.length === 1 ? '' : 's'}
              {sr.weeks.length ? ` · latest ${fmtDate(sr.weeks[0].date)}` : ''}
              {' · '}{sr.rows.length} items
            </span>
          </span>
        </button>
        <div className={`flex flex-wrap items-center gap-2 ${open ? '' : 'hidden'}`}>
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input type="checkbox" checked={onlyMoving} onChange={(e) => setOnlyMoving(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-brand focus:ring-brand" />
            Moving only
          </label>
          <select value={sort} onChange={(e) => setSort(e.target.value)}
            className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700">
            <option value="sheet">Sheet order</option>
            <option value="avg">Sort: usage</option>
            <option value="newest">Sort: on hand</option>
            <option value="name">Sort: name</option>
          </select>
          <button type="button" onClick={exportCsv} disabled={saving}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <ArrowDownTrayIcon className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Excel"}
          </button>
        </div>
      </header>

      {open && (<>
      {flagged.length > 0 && (
        <div className="border-b border-rose-200 bg-rose-50 px-4 py-2.5">
          <p className="flex items-start gap-2 text-xs font-bold uppercase tracking-wide text-rose-800">
            <ExclamationTriangleIcon className="mt-px h-4 w-4 shrink-0" />
            {flagged.length} item{flagged.length === 1 ? '' : 's'} may be partial or incorrect
          </p>
          <ul className="mt-1 space-y-0.5 pl-6">
            {flagged.map((r) => (
              <li key={r.stock_item_id} className="text-xs text-rose-700">
                <span className="font-semibold">{r.name}</span>
                <span className="font-mono text-[11px] text-rose-500"> {r.sku}</span>
                {' — '}{r.flag_reason}
              </li>
            ))}
          </ul>
          <p className="mt-1 pl-6 text-[11px] text-rose-600">
            Their averages are still shown, but check them before ordering against them.
          </p>
        </div>
      )}
      {!sr.complete && (
        <p className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          Only {sr.weeks.length} of 3 usable counts, so no weekly average can be worked out yet.
        </p>
      )}
      {sr.skipped.length > 0 && (
        <div className="border-b border-rose-200 bg-rose-50 px-4 py-2.5">
          <p className="flex items-start gap-2 text-xs font-bold uppercase tracking-wide text-rose-800">
            <ExclamationTriangleIcon className="mt-px h-4 w-4 shrink-0" />
            Partial or incorrect count — left out of the average
          </p>
          <ul className="mt-1 space-y-0.5 pl-6">
            {sr.skipped.map((s) => (
              <li key={s.date} className="text-xs text-rose-700">
                <span className="font-semibold tabular-nums">{fmtDate(s.date)}</span>
                {' — '}{s.reason}
              </li>
            ))}
          </ul>
          <p className="mt-1 pl-6 text-[11px] text-rose-600">
            Counting these would make usage look far higher or lower than it is, so the next
            complete week was used instead.
          </p>
        </div>
      )}

      {/* Scrolls in both directions inside the card. overflow-x on its own
          silently makes this the scroll container without ever scrolling
          vertically, which leaves a sticky header with nothing to stick to -
          so it needs a height and overflow-y as well. */}
      <div className="max-h-[75vh] overflow-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
              {/* Sticky both ways: the item name holds when scrolling right,
                  the dates and planning columns hold when scrolling down. The
                  corner cell must outrank both, hence z-30. */}
              <th className="sticky left-0 top-0 z-30 bg-white px-4 py-2 text-left font-semibold">Item</th>
              {weeksAsc.map((w, i) => (
                <th key={w.date} className="sticky top-0 z-20 bg-white px-3 py-2 text-right font-semibold">
                  {fmtDate(w.date)}
                  <span className="block text-[10px] font-normal normal-case text-gray-400">
                    {i === weeksAsc.length - 1
                      ? 'latest'
                      : `${i === 0 ? 'oldest' : 'middle'}`}
                  </span>
                </th>
              ))}
              <th className="sticky top-0 z-20 bg-brand-light px-3 py-2 text-right font-semibold text-brand-dark">
                Avg / week
              </th>
              <th className="sticky top-0 z-20 bg-sky-50 px-3 py-2 text-right font-semibold text-sky-900">
                Week 4
                <span className="block text-[10px] font-normal normal-case text-sky-600">forecast</span>
              </th>
              <th className="sticky top-0 z-20 bg-sky-50 px-3 py-2 text-right font-semibold text-sky-900">
                Week 5
                <span className="block text-[10px] font-normal normal-case text-sky-600">forecast</span>
              </th>
              <th className="sticky top-0 z-20 bg-violet-50 px-3 py-2 text-right font-semibold text-violet-900">
                Theoretical
                <span className="block text-[10px] font-normal normal-case text-violet-500">should hold</span>
              </th>
              <th className="sticky top-0 z-20 bg-violet-50 px-3 py-2 text-right font-semibold text-violet-900">
                On hand − theoretical
                <span className="block text-[10px] font-normal normal-case text-violet-500">negative = short</span>
              </th>
              <th className="sticky top-0 z-20 bg-emerald-50 px-3 py-2 text-right font-semibold text-emerald-900">
                Order
                <span className="block text-[10px] font-normal normal-case text-emerald-600">whole boxes</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.stock_item_id} className={r.flag ? 'bg-rose-50/60' : 'hover:bg-gray-50'}>
                <td className={`sticky left-0 z-10 px-4 py-2 ${
                  r.flag ? 'border-l-4 border-rose-400 bg-rose-50' : 'bg-white'}`}>
                  <span className="block font-medium text-gray-900">
                    {r.group_members ? r.group_label : r.name}
                    {r.flag && (
                      <span
                        title={r.flag_reason}
                        className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-100 px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wide text-rose-800"
                      >
                        <ExclamationTriangleIcon className="h-3 w-3" />
                        {r.flag === 'gap' ? 'not counted' : 'check'}
                      </span>
                    )}
                  </span>
                  <span className="block font-mono text-[11px] text-gray-400">
                    {r.group_members ? `${r.group_members.length} codes` : r.sku}
                    {r.group_label && !r.group_members ? (
                      <span className="ml-1 text-violet-400">&#8627; {r.group_label}</span>
                    ) : null}
                  </span>
                </td>
                {[...r.counts].reverse().map((c, i) => (
                  <td key={i} className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${
                    i === r.counts.length - 1 ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                    {qty(c)}
                  </td>
                ))}
                <td className={`whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums ${
                  r.avg_weekly === null ? 'text-gray-400'
                    : Number(r.avg_weekly) > 0 ? 'text-gray-900'
                      : Number(r.avg_weekly) < 0 ? 'text-emerald-700' : 'text-gray-400'}`}>
                  {dp2(r.avg_weekly)}
                </td>
                {r.forecast.map((f, i) => (
                  <td key={i} className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${
                    f === null ? 'bg-sky-50/40 text-gray-300'
                      : f <= 0 ? 'bg-rose-100 font-bold text-rose-800'
                        : 'bg-sky-50/40 text-sky-900'}`}>
                    {dp2(f)}
                  </td>
                ))}
                <td className="whitespace-nowrap bg-violet-50/40 px-3 py-2 text-right tabular-nums text-violet-900">
                  {r.theoretical === null ? '—' : Number(r.theoretical).toLocaleString('en-GB')}
                  {r.group_members ? (
                    <span
                      title={`${r.group_label}: ${r.group_members.map((m) => `${m.sku} ${m.on_hand === null ? '—' : m.on_hand}`).join(' + ')} = ${r.group_on_hand}`}
                      className="ml-1 cursor-help rounded bg-violet-100 px-1 text-[10px] font-semibold text-violet-700"
                    >
                      all {r.group_members.length}
                    </span>
                  ) : null}
                </td>
                <td className={`whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums ${
                  r.on_hand_vs_theoretical === null ? 'bg-violet-50/40 text-gray-300'
                    : r.on_hand_vs_theoretical < 0 ? 'bg-rose-100 text-rose-800'
                      : 'bg-emerald-50 text-emerald-800'}`}>
                  <span title={r.on_hand_basis
                    ? `on hand ${r.group_on_hand !== null ? `${r.group_on_hand} (all ${r.group_members.length} codes)` : `${r.on_hand} (${r.on_hand_basis})`} − theoretical ${r.theoretical}`
                    : undefined}>
                    {r.on_hand_vs_theoretical === null
                      ? '—'
                      : Number(r.on_hand_vs_theoretical).toLocaleString('en-GB')}
                    {r.on_hand_basis === 'no movement' ? <span title="No usage in the window, so the last count is carried forward" className="ml-1 cursor-help text-[10px] font-normal text-violet-500">flat</span> : null}
                  </span>
                </td>
                <td className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${
                  r.order_qty ? 'bg-emerald-50 font-bold text-emerald-900' : 'bg-emerald-50/30 text-gray-300'}`}>
                  {r.order_qty ? Number(r.order_qty).toLocaleString('en-GB') : '—'}
                  {r.order_options ? (
                    <span className="mt-0.5 block space-y-0.5">
                      {r.order_options.map((o) => (
                        <span key={o.sku} className={`block text-[10px] font-normal ${
                          o.stocked ? 'font-semibold text-emerald-700' : 'text-gray-500'}`}>
                          {o.boxes} &times; {o.box} = {o.qty} {o.label.replace(/ ?[0-9]+M$/i, '')}
                          {o.stocked ? ' ✓' : ''}
                        </span>
                      ))}
                    </span>
                  ) : r.boxes ? (
                    <span className="block text-[10px] font-normal text-emerald-600">
                      {r.boxes} &times; {r.box_size} ({r.box_label})
                    </span>
                  ) : null}
                  {r.order_qty && !r.boxes ? (
                    <span className="block text-[10px] font-normal text-amber-600" title="No box size recorded for this item">no box size</span>
                  ) : null}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={sr.weeks.length + 7} className="px-4 py-6 text-center text-sm text-gray-500">
                Nothing to show with this filter.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      </>)}
    </section>
  );
}

function IspStockCounts() {
  const { data: isps } = useIsps();
  const [ispId, setIspId] = useState(null);
  const active = ispId || (isps && isps[0] && isps[0].id) || null;
  const { data, isLoading, isError, error } = useIspStockCounts(active);

  // Closed by default — nine storerooms of up to 81 rows is an unreadable
  // page, and the header carries the bad-count flag so nothing important is
  // hidden by being collapsed.
  const [openIds, setOpenIds] = useState(() => new Set());
  const toggle = (id) => setOpenIds((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allOpen = Boolean(data?.storerooms?.length)
    && data.storerooms.every((s) => openIds.has(s.id));
  const toggleAll = () => setOpenIds(allOpen ? new Set() : new Set(data.storerooms.map((s) => s.id)));

  const ispName = data?.isp?.name || '';

  const [saving, setSaving] = useState(false);
  const exportAll = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await writeWorkbook(`${ispName} - stock count.xlsx`,
        `${data.isp.name} Stock Count (Active)`,
        data.storerooms.map(sheetBlock));
    } finally { setSaving(false); }
  };

  return (
    <div className="w-full px-4 py-6 xl:px-6">
      <Link to="/" className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to dashboard
      </Link>

      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ISP stock counts</h1>
          <p className="mt-0.5 text-sm text-gray-600">
            The last three weekly storeroom counts, and the average weekly usage they imply
            <span className="text-gray-400"> — (oldest − latest) ÷ 3</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(isps || []).map((i) => (
            <button
              key={i.id} type="button" onClick={() => setIspId(i.id)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                active === i.id
                  ? 'bg-brand text-white'
                  : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {i.name}
            </button>
          ))}
          {data && (
            <button type="button" onClick={exportAll} disabled={saving}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <ArrowDownTrayIcon className="h-4 w-4" /> {saving ? "Building…" : "Export to Excel"}
            </button>
          )}
        </div>
      </div>

      {isError && (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error?.response?.data?.error || 'Could not load the counts.'}
        </p>
      )}
      {isLoading && <p className="text-sm text-gray-500">Loading counts…</p>}

      {data && (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              {data.isp.name} · {data.active_skus} items on the count form · a week is left out if
              it holds under {Math.round(data.min_share * 100)}% of that storeroom's normal volume
            </p>
            {data.storerooms.length > 0 && (
              <button type="button" onClick={toggleAll}
                className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                {allOpen ? 'Collapse all' : 'Expand all'}
              </button>
            )}
          </div>
          {data.storerooms.map((sr) => (
            <Storeroom
              key={sr.id} sr={sr} ispName={data.isp.name}
              open={openIds.has(sr.id)} onToggle={() => toggle(sr.id)}
            />
          ))}
          {data.storerooms.length === 0 && (
            <p className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
              No storeroom counts for {data.isp.name} yet.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default IspStockCounts;
